"use client";

import React from "react";

/**
 * Glifo das setas, compartilhado pelo carrossel e pelo modal.
 *
 * Desenhado com STROKE, não com path preenchido. O path anterior tinha
 * espessura fixa embutida na forma (9,4% do viewBox, ou seja 1,5px num ícone de
 * 16px) e ficava visivelmente mais grosso que a borda de 1px do botão — os dois
 * traços conviviam no mesmo círculo sem casar. Com stroke a espessura é um
 * número que acompanha a borda.
 */

/**
 * Compensação ÓPTICA.
 *
 * Um chevron tem a massa concentrada no vértice; centrado geometricamente, o
 * olho o lê como deslocado para o lado da abertura. O deslocamento empurra o
 * glifo de volta na direção da ponta. É a mesma correção que se faz no ícone de
 * "play" dentro de um círculo.
 *
 * Em unidades do viewBox de 24: 0.75 ≈ 0,5px num ícone de 16px.
 */
const OPTICAL_SHIFT = 0.75;

export type ArrowGlyphShape = "chevron" | "arrowShort" | "arrow";

type ArrowGlyphProps = {
  /**
   * `chevron` é o clássico (›); `arrowShort` é a seta compacta e `arrow` a
   * alongada — as duas têm haste, o que muda é quanto ela avança.
   */
  shape?: ArrowGlyphShape;
  /** Espelha para apontar à direita. */
  flip?: boolean;
  /**
   * Espessura do traço, em unidades do viewBox de 24.
   *
   * 2 é o padrão de ícone da web (lucide, feather). NÃO se calibra a espessura
   * pela borda do botão: o ícone tem peso próprio, e casar o traço com 1px de
   * borda — como estava aqui — deixa o glifo fino a ponto de sumir dentro do
   * disco.
   */
  strokeWidth?: number;
};

export const ArrowGlyph = ({
  shape = "chevron",
  flip,
  strokeWidth = 2,
}: ArrowGlyphProps) => {
  /*
   * A seta com haste já é simétrica no eixo horizontal (a linha atravessa o
   * viewBox inteiro), então não precisa — nem deve — de compensação óptica:
   * deslocá-la só a tiraria do lugar.
   */
  const shift = shape === "chevron" ? OPTICAL_SHIFT : 0;
  const transforms = [
    flip ? "scaleX(-1)" : null,
    shift ? `translateX(${-shift}px)` : null,
  ].filter(Boolean);

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={transforms.length > 0 ? { transform: transforms.join(" ") } : undefined}
      aria-hidden
    >
      {shape === "arrow" || shape === "arrowShort" ? (
        <>
          {/*
            É a razão haste/ponta que separa as duas: a alongada vai de borda a
            borda com ponta curta; a simples tem haste mais curta e ponta
            proporcionalmente maior, o desenho de seta convencional.
          */}
          {shape === "arrow" ? (
            <>
              <line x1="22" y1="12" x2="2" y2="12" />
              <polyline points="8,7 2,12 8,17" />
            </>
          ) : (
            <>
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="11,6 5,12 11,18" />
            </>
          )}
        </>
      ) : (
        <polyline points="15,4 7,12 15,20" />
      )}
    </svg>
  );
};
